'use client';
import { useEffect, useRef, useState } from 'react';
import { clsx } from 'clsx';
import { Episode } from 'components/UI/Episode';
import Image, { type ApiImageProps } from 'components/UI/Image/Image';
import type { TitleLink } from 'components/UI/Title';
import { Title } from 'components/UI/Title';
import { generateWeakKey } from 'utils/idGenerator';
import { slugify } from 'utils/string';
import styles from './rtl-plus-letzte-folgen.module.scss';

export type WidgetTeaserRtlPlusLastEpisodesProps = {
  readonly model?: {
    readonly fields?: {
      readonly title?: string;
      readonly link?: TitleLink;
      readonly anchor?: string;
    };
  };
  readonly items?: ReadonlyArray<{
    readonly model: {
      readonly id: string;
      readonly utm_URL?: string;
      readonly fields: {
        readonly title: string;
        readonly 'short-text'?: string;
        readonly 'start-date'?: string;
        readonly url: string;
        readonly 'is-premium'?: boolean | string;
      };
      readonly image?: ApiImageProps;
    };
  }>;
};

export const WidgetTeaserRtlPlusLastEpisodes = ({ context }: { context: WidgetTeaserRtlPlusLastEpisodesProps }) => {
  const gridRef = useRef<HTMLDivElement>(null);
  const [activeIndicator, setActiveIndicator] = useState(0);

  const width = 440;

  const handleScroll = () => {
    const gridCurrentRef = gridRef.current;

    if (gridCurrentRef !== null) {
      const { scrollLeft, children } = gridCurrentRef;
      const teaserWidth = children[0].clientWidth;
      const activeIndex = Math.round(scrollLeft / teaserWidth);

      setActiveIndicator(activeIndex);
    }
  };

  useEffect(() => {
    const gridCurrentRef = gridRef.current;

    gridCurrentRef?.addEventListener('scroll', handleScroll, { passive: true });

    return () => {
      gridCurrentRef?.removeEventListener('scroll', handleScroll);
    };
  }, []);

  return (
    <section className={styles.section} id={context.model?.fields?.anchor}>
      {context.model?.fields?.title && <Title link={context.model?.fields?.link}>{context.model?.fields?.title}</Title>}
      <div className={styles.contentWrapper}>
        <div className={styles.grid} ref={gridRef}>
          {context.items?.map((item, index) => {
            const slug = slugify(context.model?.fields?.title ?? 'rtl-plus-letzte-folgen');

            return (
              <div key={item.model.id} className={clsx(styles.teaser, { [styles.first]: index === 0 })}>
                <div className={styles.imageWrapper}>
                  <Image
                    image={item.model.image}
                    className={styles.image}
                    alt={item.model.fields.title}
                    width={width}
                  />
                  <div className={styles.badge}>{item.model.fields['is-premium'] === 'true' ? 'Premium' : 'Free'}</div>
                </div>
                <Episode
                  id={`${slug}-teaser${index + 1}`}
                  url={item.model.utm_URL ?? item.model.fields.url}
                  title={item.model.fields.title}
                  shortText={item.model.fields['short-text']}
                  index={index}
                  startDate={item.model.fields['start-date']}
                />
              </div>
            );
          })}
        </div>
        <div className={styles.indicatorWrapper}>
          {context.items?.map((item, index) => (
            <div
              key={`${generateWeakKey(item, 'teaser-rtl-plus-letzte-folgen-indicator')}`}
              className={clsx(styles.indicator, { [styles.active]: activeIndicator === index })}
            />
          ))}
        </div>
      </div>
    </section>
  );
};
